import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Checkbox,
  colors,
  Expander,
  ExpanderContent,
  InlineButton,
  LayoutBand,
  List,
  useStatusOverlay,
  H5,
  Subheading,
  Separator,
} from '@fs/zion-ui'
import { i18n } from '@fs/zion-locale'
import { Notice } from '@fs/zion-icon'
import { useRandomId } from '@fs/zion-frontend-friends'
import { css } from '@emotion/core'
import { useQueryParams } from '@fs/zion-router'
import { getPreference, setPreference } from '@fs/zion-preferences'
import { trackLink } from '@fs/zion-analytics'
import ListItemGroupData from '../ListItemGroupData/ListItemGroupData'
import transl from './VolumeSetMetadata.transl'
import { useBrowseProviderContext } from '../../providers/BrowseProvider'

const MY_JIAPU_PREFERENCE_KEY = 'chinese.myJiapu'

const labelCss = css`
  color: ${colors.transparent.gray80};
  font-size: 12px;
  line-height: 20px;
  padding: 4px inherit;
  text-transform: uppercase;
`

export default function VolumeSetMetadata({ groupId, metadata }) {
  const {
    query: { volumeSetSearch },
  } = useQueryParams()
  const { dispatch } = useBrowseProviderContext()
  const id = useRandomId()
  const [myJiapuIds, setMyJiapuIds] = useState([])
  const [open, setOpen] = useState(false)
  const showStatusOverlay = useStatusOverlay()

  useEffect(() => {
    dispatch({ type: 'SET_SHOW_ITEM_BREADCRUMBS', value: false })
  }, [dispatch])

  useEffect(() => {
    if (['jiapu', 'jokbo', 'keizu'].includes(volumeSetSearch)) {
      showStatusOverlay({
        dismissable: true,
        Icon: Notice,
        // locale-cleaner: VolumeSetMetadata.jiapu VolumeSetMetadata.jokbo VolumeSetMetadata.keizu
        message: transl.toast(i18n.t(`VolumeSetMetadata.${volumeSetSearch}`, volumeSetSearch)),
      })
    }
  }, [showStatusOverlay, volumeSetSearch])

  useEffect(() => {
    if (volumeSetSearch === 'jiapu') {
      getPreference(MY_JIAPU_PREFERENCE_KEY).then((jiapuList) => {
        if (Array.isArray(jiapuList)) {
          setMyJiapuIds(jiapuList)
        }
      })
    }
  }, [groupId, volumeSetSearch])

  function toggleMyJiapu({ target: { checked } }) {
    trackLink({ name: `Checkbox:My volumeSet:Toggle-${groupId}` })
    const newMyJiapuList = [...myJiapuIds]

    if (checked) {
      newMyJiapuList.push(groupId)
    } else {
      newMyJiapuList.splice(newMyJiapuList.indexOf(groupId), 1)
    }

    setPreference(MY_JIAPU_PREFERENCE_KEY, newMyJiapuList)
    setMyJiapuIds(newMyJiapuList)
  }

  const headings = { jiapu: transl.jiapuHeading(), jokbo: transl.jokboHeading(), keizu: transl.keizuHeading() }
  return (
    <>
      {volumeSetSearch === 'jiapu' && (
        <LayoutBand alignY="middle" color="#f6f6f6" minHeight={80}>
          <Checkbox
            checked={myJiapuIds.includes(groupId)}
            label={i18n.t('VolumeSetMetadata.checkbox.thisIsMyJiapu', 'This is my jiapu')}
            name="myJiapuCheckbox"
            onChange={toggleMyJiapu}
          />
        </LayoutBand>
      )}

      <Separator size="xs" />

      <H5 supplementary>
        {headings[volumeSetSearch]}
        <Subheading>{transl.subHeading(metadata?.volumeCount.toLocaleString())}</Subheading>
      </H5>

      <Separator size="xs" />

      {metadata && (
        <List>
          <ListItemGroupData items={[metadata.title]} overline={transl.title()} />
          <ListItemGroupData items={[metadata.surnames]} overline={transl.surnames()} />
          {volumeSetSearch === 'jokbo' && (
            <>
              <ListItemGroupData items={[metadata.clan]} overline={transl.clan()} />
              <ListItemGroupData items={metadata.clanBranches} overline={transl.clanBranches()} />
            </>
          )}
          <ListItemGroupData items={[metadata.volumeCount]} overline={transl.volumes()} />
          <ListItemGroupData items={[metadata.firstAncestor]} overline={transl.firstAncestor()} />
          <ListItemGroupData items={[metadata.migrantAncestor]} overline={transl.migrantAncestor()} />
          {volumeSetSearch === 'jiapu' && (
            <ListItemGroupData items={[metadata.ancestralHall]} overline={transl.ancestralHall()} />
          )}
          <ListItemGroupData items={metadata.authors} overline={transl.author()} />

          <span css={labelCss}>{transl.notes()}</span>
          {metadata.notes.length > 3 ? (
            <>
              {!open && metadata.notes.slice(0, 3).map((note) => <div key={note}>{note}</div>)}

              <Expander ariaControlsId={id} open={open} setOpen={setOpen}>
                <ExpanderContent>{open && metadata.notes.map((note) => <div key={note}>{note}</div>)}</ExpanderContent>

                <InlineButton aria-controls={id} onClick={() => setOpen(!open)}>
                  {open ? i18n.t('common-ui:show-less.info.abbr') : i18n.t('common-ui:show-more.info.abbr')}
                </InlineButton>
              </Expander>
            </>
          ) : (
            metadata.notes.map((note) => <div key={note}>{note}</div>)
          )}

          {volumeSetSearch === 'jiapu' && (
            <ListItemGroupData items={[metadata.generationPoem]} overline={transl.generationPoem()} />
          )}
        </List>
      )}
    </>
  )
}

VolumeSetMetadata.propTypes = {
  groupId: PropTypes.string.isRequired,
  metadata: PropTypes.shape({
    ancestralHall: PropTypes.string,
    authors: PropTypes.arrayOf(PropTypes.string),
    firstAncestor: PropTypes.string,
    generationPoem: PropTypes.string,
    migrantAncestor: PropTypes.string,
    notes: PropTypes.arrayOf(PropTypes.string),
    surname: PropTypes.string,
    title: PropTypes.string,
    volumeCount: PropTypes.number,
  }),
}
